import { Injectable } from "@angular/core";
import { BehaviorSubject, filter, map, tap } from "rxjs";
import { Router } from "@angular/router";
import { environment } from "src/environments/environment";
import { environment as prodEnvironment } from "src/environments/environment.prod";
import { HttpClient, HttpErrorResponse, HttpEventType, HttpHeaders, HttpResponse } from "@angular/common/http";

//interfaces
import { OrderInterface, ArticleInterface, ShopInterface, TransactionInterface, LocalArticleInterface } from "../interfaces";

//enums
import { PaymentStatusEnum } from "../enums/payment-status.enum";
import { ValidationStatusEnum } from "../enums/index";
import { StorageHelper } from "../helpers";
import { OrderPreviewInterface } from "../interfaces/order-preview.interface";

@Injectable({
  providedIn: 'root'
})
export class OrderService {
  public static shop: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  public static shopCode: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  public static kioskApiKey: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  public static employee: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public static order: BehaviorSubject<OrderInterface | null> = new BehaviorSubject<OrderInterface | null>(null);
  public static fallback: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public static orderPreview: BehaviorSubject<{ new: number | null, order: OrderPreviewInterface | null }> = new BehaviorSubject<{ new: number | null, order: OrderPreviewInterface | null }>({ new: null, order: null });
  public static loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public static errorMessage: BehaviorSubject<HttpErrorResponse | null> = new BehaviorSubject<HttpErrorResponse | null>(null);

  public loadingProgress = new BehaviorSubject<number>(0);

  private keyHeaders = new HttpHeaders({ 'x-api-key': '40c90ccb-dde2-4d97-96e8-88e15cab68fd' });
  private urlHeaders = new HttpHeaders({ 'x-api-key': '40c90ccb-dde2-4d97-96e8-88e15cab68fd', 'Content-Type': 'application/x-www-form-urlencoded' });
  private readonly _apiUrl = environment.api_url;
  private readonly _herokuUrl = environment.heroku_url;

  constructor(
    private router: Router,
    private http: HttpClient,
    private storageHelper: StorageHelper
  ) { }

  getArticles() {
    return this.http.get<{ items: LocalArticleInterface[] }>(
      this._herokuUrl + `internal/get_articles_barcode?page=0`,
      { headers: this.keyHeaders, reportProgress: true, observe: 'events' }
    ).pipe(
      tap(event => {
        console.log(event)
        if (event.type === HttpEventType.DownloadProgress && event.total) {
          let progress = Math.round((event.loaded / event.total) * 100)
          if (progress == 0) progress = 1;
          this.loadingProgress.next(progress);
        }
      }),
      filter((event): event is HttpResponse<{ items: LocalArticleInterface[] }> => event instanceof HttpResponse),
      map(event => event.body!)
    );
  }

  restart() {
    OrderService.order.next(null);
    OrderService.orderPreview.next({ new: null, order: null });
    this.router.navigate(['/home']);
  }


  getShops() {
    return this.http.get<ShopInterface[]>(this._apiUrl + `internal_wond/getShops`, { headers: this.keyHeaders });
  }

  getArticleByBarcode(barcode: string) {
    let order = new OrderInterface();
    order.itemList = [{ article: { barcode: barcode, quantity: 1 } }];
    const body = { data: order, is_employee: OrderService.employee.value };
    return this.http.post<OrderPreviewInterface>(this._apiUrl + `internal_wond/calculateOrder`, body, { headers: this.keyHeaders });
  }

  getArticlesByCategory(categoryId: string) {
    return this.http.get<{ category: string, articleList: ArticleInterface[] }>(this._apiUrl + `internal_wond/getArticlesByCategory?category_id=${categoryId}`, { headers: this.keyHeaders });
  }

  calculateOrder() {
    OrderService.loading.next(true);
    const body = { data: OrderService.order.value, is_employee: OrderService.employee.value };
    return this.http.post<OrderPreviewInterface>(this._apiUrl + `internal_wond/calculateOrder`, body, { headers: this.keyHeaders })
  }

  checkout() {
    let body = { data: OrderService.order.value, api_key: OrderService.kioskApiKey.getValue(), is_employee: OrderService.employee.value };
    return this.http.post<{ guid: string }>(this._apiUrl + `internal_wond/createOrder`, body, { headers: this.keyHeaders })
  }

  getCheckoutStatus(guid: string) {
    return this.http.get<TransactionInterface>(this._apiUrl + `internal_wond/pullTransactionStatus?guid=${guid}`, { headers: this.keyHeaders });
  }

  getRestrictionStatus() {
    const kioskApiKey = OrderService.kioskApiKey.getValue();
    return this.http.get<{ device: { state: ValidationStatusEnum } }>(this._apiUrl + `devices/device_state?device_api_key=${kioskApiKey}`, { headers: this.keyHeaders });
  }

  resetRestrictionStatus() {
    const kioskApiKey = OrderService.kioskApiKey.getValue();
    return this.http.get<{ device: { state: ValidationStatusEnum } }>(this._apiUrl + `devices/update_device_state?device_api_key=${kioskApiKey}&setstate=AWAITING`, { headers: this.keyHeaders });
  }

  retryOrder(guid: string) {
    return this.http.get<{ status: PaymentStatusEnum }>(this._apiUrl + `internal_wond/retryOrder?guid=${guid}`, { headers: this.keyHeaders });
  }

  getOrder(key: string) {
    return this.http.get<{ key: string, message: string }>(prodEnvironment.api_url + `transfermessage?key=${key}`, { headers: this.urlHeaders });
  }

  checkEmployeeNr(key: string) {
    return this.http.get<{ is_employee: boolean }>(this._apiUrl + `internal/searchEmployee?rfid_uid=${key}`, { headers: this.keyHeaders });
  }
}  